import type { ExerciseTemplate, Trap } from '@yuan-math/exercises';
import { fraction, identical, toLatex, toText, type Fraction } from '@yuan-math/math';
import {
  choiceExercise,
  expr,
  expressionExercise,
  leadingTerm,
  fractionExercise,
  fractionTrap,
  numericExercise,
  options,
  signedConstant,
  signedTerm,
  template,
  trap,
  within,
} from './authoring.ts';

/**
 * Trigonometric identities, the addition formulas, trigonometric equations, the
 * sine and cosine rules, the conics, vectors, polar coordinates, limits and the
 * rate of change.
 *
 * Several of these are built backwards from the wrong answer, because a vaguely
 * wrong distractor would diagnose nothing.
 *
 * `pythagorean-identity-square` asks for cos²θ rather than for cos θ. The
 * misunderstanding it is aimed at — writing cos² = 1 + sin² — produces an
 * irrational number once the root is taken, which a student cannot type and the
 * grader would never see. Squared, the same error produces an exact fraction
 * that names itself.
 *
 * `cosine-rule-side` keeps the angle acute so that the misread
 * (a² + b² − 2ab)·cos C stays positive and has a real square root. With an
 * obtuse angle the wrong route gives a negative under the root, the student
 * gets no answer at all, and the item diagnoses nothing.
 *
 * `ambiguous-case-obtuse` chooses the triangle so that both the acute and the
 * obtuse angle really do fit the given measurements. A student reporting the
 * acute one has not made an arithmetic slip; they have answered a different
 * question correctly, which is the only way this misconception ever shows up.
 *
 * `polar-to-cartesian` asks only for the coordinate that comes out rational. At
 * 120° the across-coordinate is −r/2 and the up-coordinate is r√3/2; asking for
 * the second would be asking for something no student can type into an answer
 * box, and the item would be testing notation rather than trigonometry.
 *
 * `rate-from-two-readings` restricts the rate to values whose reciprocal
 * terminates. The misconception it hunts is the upside-down quotient, and at a
 * rate of 3 that wrong answer is 0.333…, which nobody writes down — a trap no
 * student can land on catches no student.
 */

/** Right triangles with whole-number sides, for exact trigonometric values. */
const TRIPLES: readonly [number, number, number][] = [
  [3, 4, 5],
  [5, 12, 13],
  [8, 15, 17],
  [7, 24, 25],
  [20, 21, 29],
  [9, 40, 41],
];

/** Rounds to one decimal place, the precision these answers are asked to. */
const round1 = (value: number): number => Math.round(value * 10) / 10;

const radians = (degrees: number): number => (degrees * Math.PI) / 180;

/**
 * A number for prose rather than for LaTeX.
 *
 * `-7` inside `\\frac{}{}` is right and `-7` in a sentence is a hyphen where a
 * minus sign belongs. Explanations are rendered as text, so they need the real
 * character.
 */
const minus = (value: number | string): string => String(value).replace(/-/g, '\u2212');

/** `ellipse` → `An ellipse`, so an option reads as an answer rather than a tag. */
const titled = (name: string): string => `${/^[aeiou]/.test(name) ? 'An' : 'A'} ${name}`;

/**
 * Drops any trap whose value repeats the answer or an earlier trap.
 *
 * Two traps that normalise to the same response are indistinguishable —
 * whichever is listed first wins, and the student is told about an error they
 * may not have made. Generated parameters make that easy to author by accident:
 * the addition-formula items build two wrong answers from the same four
 * products, and a triple can make them coincide.
 */
const distinctFractionTraps = (answer: Fraction, entries: { value: Fraction; misconceptionId: string }[]): Trap[] => {
  const kept: Fraction[] = [answer];
  const out: Trap[] = [];
  for (const entry of entries) {
    if (kept.some((seen) => identical(seen, entry.value))) continue;
    kept.push(entry.value);
    out.push(fractionTrap(entry.value, entry.misconceptionId));
  }
  return out;
};

export const PRECALCULUS_TEMPLATES: ExerciseTemplate[] = [
  /* ------------------------------------------------------- trig identities */
  template({
    id: 'pythagorean-identity-square',
    conceptId: 'trig-identities',
    description: 'Use sin²θ + cos²θ = 1, in the direction that subtracts.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const [o, a, h] = rng.pick(difficulty === 3 ? TRIPLES : TRIPLES.slice(0, 4));
      const wantCosine = rng.bool();
      const known = wantCosine ? o : a;
      const other = wantCosine ? a : o;
      const knownName = wantCosine ? 'sin' : 'cos';
      const wantedName = wantCosine ? 'cos' : 'sin';

      return fractionExercise({
        prompt: `${knownName} θ = ${known}/${h}. What is ${wantedName}²θ?`,
        latex: `\\${knownName}\\theta = \\frac{${known}}{${h}}`,
        value: fraction(other * other, h * h),
        requireSimplified: true,
        summary: `${knownName}²θ = ${known * known}/${h * h}, and the two squares add to 1, so ${wantedName}²θ = 1 − ${known * known}/${h * h} = ${toText(fraction(other * other, h * h))}.`,
        hint: 'The two squares add to 1, so each one is 1 minus the other.',
        steps: [
          { label: 'Square the value you were given.', latex: `\\${knownName}^{2}\\theta = \\frac{${known * known}}{${h * h}}` },
          { label: 'Subtract it from 1.', latex: `\\${wantedName}^{2}\\theta = 1 - \\frac{${known * known}}{${h * h}}` },
          { label: 'Evaluate.', latex: `\\frac{${other * other}}{${h * h}}` },
        ],
        // Rearranging the identity by adding: cos² = 1 + sin².
        traps: [fractionTrap(fraction(h * h + known * known, h * h), 'pythagorean-identity-rearranged-wrongly')],
      });
    },
  }),

  template({
    id: 'identity-simplify',
    conceptId: 'trig-identities',
    description: 'Simplify an expression by spotting the Pythagorean pair.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const forms = [
        {
          latex: '\\frac{1 - \\cos^{2}\\theta}{\\sin\\theta}',
          correct: '\\sin\\theta',
          wrongRearrangement: '\\frac{1}{\\sin\\theta}',
          summary: '1 − cos²θ is sin²θ, and sin²θ ÷ sin θ leaves sin θ.',
        },
        {
          latex: '\\frac{1 - \\sin^{2}\\theta}{\\cos\\theta}',
          correct: '\\cos\\theta',
          wrongRearrangement: '\\frac{1}{\\cos\\theta}',
          summary: '1 − sin²θ is cos²θ, and cos²θ ÷ cos θ leaves cos θ.',
        },
        {
          latex: '\\sin^{2}\\theta + \\cos^{2}\\theta + 1',
          correct: '2',
          wrongRearrangement: '1',
          summary: 'The first two terms are 1 between them, so the whole expression is 1 + 1 = 2.',
        },
      ];
      const form = rng.pick(forms);

      return choiceExercise({
        prompt: 'Simplify as far as it will go.',
        latex: form.latex,
        ...options(
          [
            { latex: form.correct, correct: true },
            { latex: form.wrongRearrangement, misconceptionId: 'pythagorean-identity-rearranged-wrongly' },
            { latex: '\\tan\\theta' },
            { latex: '0' },
          ],
          rng.shuffle,
        ),
        summary: form.summary,
        hint: 'Look for sin²θ + cos²θ, or for 1 minus one of them.',
      });
    },
  }),

  template({
    id: 'identity-or-equation',
    conceptId: 'trig-identities',
    description: 'Tell an identity apart from an equation.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const equations = [
        '\\sin\\theta = \\tfrac{1}{2}',
        '\\cos\\theta = 0',
        '\\tan\\theta = 1',
        '\\sin\\theta = \\cos\\theta',
      ];
      const identity = rng.pick([
        '\\sin^{2}\\theta + \\cos^{2}\\theta = 1',
        '\\tan\\theta = \\frac{\\sin\\theta}{\\cos\\theta}',
      ]);
      const wrong = rng.shuffle(equations).slice(0, 2);

      return choiceExercise({
        prompt: 'Which of these is an identity — true for every angle?',
        ...options(
          [
            { latex: identity, correct: true },
            { latex: wrong[0] as string, misconceptionId: 'identity-treated-as-equation' },
            { latex: wrong[1] as string, misconceptionId: 'identity-treated-as-equation' },
            /*
             * Not an equation that happens to be false — a rule a student
             * believes holds everywhere, which is why it belongs in a question
             * about what "everywhere" means.
             */
            {
              latex: '\\sin(A + B) = \\sin A + \\sin B',
              misconceptionId: 'trig-function-distributed-over-sum',
            },
          ],
          rng.shuffle,
        ),
        summary:
          'An identity holds for every angle, so there is nothing to solve for. Two of the others are true only at particular angles, and the last is true at almost none — sin does not distribute across a sum.',
        hint: 'Test each one at θ = 0 and again at θ = 90°. An identity survives both.',
      });
    },
  }),

  /* ------------------------------------------ sum, difference, double angle */
  template({
    id: 'addition-formula-value',
    conceptId: 'trig-addition-formulas',
    description: 'Expand sin(A ± B) or cos(A ± B) from two acute angles given by right triangles.',
    difficulties: [3, 4, 5],
    build: ({ rng, difficulty }) => {
      const pool = difficulty === 3 ? TRIPLES.slice(0, 4) : TRIPLES;
      const [oA, aA, hA] = rng.pick(pool);
      const [oB, aB, hB] = rng.pick(pool);
      const wantSine = rng.bool();
      const plus = rng.bool();
      const bracket = plus ? '+' : '-';
      const denominator = hA * hB;

      /*
       * sin(A ± B) = sinA cosB ± cosA sinB — the sign of the bracket, kept.
       * cos(A ± B) = cosA cosB ∓ sinA sinB — the sign of the bracket, reversed.
       */
      const firstProduct = wantSine ? oA * aB : aA * aB;
      const secondProduct = wantSine ? aA * oB : oA * oB;
      const inner = wantSine === plus ? 1 : -1;
      const between = inner === 1 ? '+' : '-';

      const value = fraction(firstProduct + inner * secondProduct, denominator);
      const name = wantSine ? 'sin' : 'cos';
      const firstLatex = wantSine ? '\\sin A\\cos B' : '\\cos A\\cos B';
      const secondLatex = wantSine ? '\\cos A\\sin B' : '\\sin A\\sin B';

      // sin A ± sin B, or cos A ± cos B: the function spread across the bracket.
      const known = wantSine ? oA : aA;
      const other = wantSine ? oB : aB;
      const distributed = fraction(known * hB + (plus ? 1 : -1) * other * hA, denominator);

      return fractionExercise({
        prompt: `A and B are acute angles with sin A = ${oA}/${hA} and cos B = ${aB}/${hB}. Find ${name}(A ${bracket} B).`,
        latex: `\\sin A = \\frac{${oA}}{${hA}}, \\quad \\cos B = \\frac{${aB}}{${hB}}`,
        value,
        requireSimplified: true,
        summary: `Complete each angle first: cos A = ${aA}/${hA} and sin B = ${oB}/${hB}, both positive because A and B are acute. Then ${name}(A ${minus(bracket)} B) = ${firstProduct}/${denominator} ${minus(between)} ${secondProduct}/${denominator} = ${minus(toText(value))}.`,
        hint: 'You need all four of sin A, cos A, sin B and cos B. Get the two missing ones from sin² + cos² = 1 before opening the formula.',
        steps: [
          { label: 'Complete angle A.', latex: `\\cos A = \\frac{${aA}}{${hA}}`, note: 'A is acute, so the cosine is positive.' },
          { label: 'Complete angle B.', latex: `\\sin B = \\frac{${oB}}{${hB}}` },
          {
            label: 'Write the formula.',
            latex: `\\${name}(A ${bracket} B) = ${firstLatex} ${between} ${secondLatex}`,
            note: wantSine ? 'Sine keeps the sign of the bracket.' : 'Cosine reverses the sign of the bracket.',
          },
          { label: 'Substitute all four values.', latex: `\\frac{${firstProduct}}{${denominator}} ${between} \\frac{${secondProduct}}{${denominator}}` },
          { label: 'Combine.', latex: toLatex(value) },
        ],
        traps: distinctFractionTraps(value, [
          // The sign between the two products taken the wrong way round.
          { value: fraction(firstProduct - inner * secondProduct, denominator), misconceptionId: 'addition-formula-sign-flipped' },
          // The function spread across the bracket, as if it multiplied.
          { value: distributed, misconceptionId: 'trig-function-distributed-over-sum' },
        ]),
      });
    },
  }),

  template({
    id: 'double-angle-sine',
    conceptId: 'trig-addition-formulas',
    description: 'Use sin 2θ = 2 sin θ cos θ on an acute angle.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const [o, a, h] = rng.pick(TRIPLES);
      const giveSine = rng.bool();
      const givenName = giveSine ? 'sin' : 'cos';
      const given = giveSine ? o : a;
      const value = fraction(2 * o * a, h * h);

      return fractionExercise({
        prompt: `θ is an acute angle with ${givenName} θ = ${given}/${h}. Find sin 2θ.`,
        latex: `\\${givenName}\\theta = \\frac{${given}}{${h}}`,
        value,
        requireSimplified: true,
        summary: `The missing value is ${giveSine ? 'cos' : 'sin'} θ = ${giveSine ? a : o}/${h}. Then sin 2θ = 2 sin θ cos θ = 2 × ${o}/${h} × ${a}/${h} = ${toText(value)}.`,
        hint: 'The 2 doubles the angle, not the answer. Which formula turns sin 2θ into something about θ?',
        steps: [
          { label: 'Complete the angle.', latex: `\\${giveSine ? 'cos' : 'sin'}\\theta = \\frac{${giveSine ? a : o}}{${h}}` },
          { label: 'Write the double-angle formula.', latex: '\\sin 2\\theta = 2\\sin\\theta\\cos\\theta' },
          { label: 'Substitute.', latex: `2 \\cdot \\frac{${o}}{${h}} \\cdot \\frac{${a}}{${h}}` },
          { label: 'Evaluate.', latex: toLatex(value) },
        ],
        traps: distinctFractionTraps(value, [
          // Doubling the value instead of the angle.
          { value: fraction(2 * given, h), misconceptionId: 'double-angle-taken-as-doubling' },
        ]),
      });
    },
  }),

  template({
    id: 'double-angle-cosine',
    conceptId: 'trig-addition-formulas',
    description: 'Use cos 2θ = cos²θ − sin²θ, in the form the given value suits.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const [o, a, h] = rng.pick(TRIPLES);
      const giveSine = rng.bool();
      const given = giveSine ? o : a;
      const givenName = giveSine ? 'sin' : 'cos';
      const value = fraction(a * a - o * o, h * h);
      const formula = giveSine ? '\\cos 2\\theta = 1 - 2\\sin^{2}\\theta' : '\\cos 2\\theta = 2\\cos^{2}\\theta - 1';
      const substituted = giveSine
        ? `1 - 2 \\cdot \\frac{${o * o}}{${h * h}}`
        : `2 \\cdot \\frac{${a * a}}{${h * h}} - 1`;

      return fractionExercise({
        prompt: `θ is an acute angle with ${givenName} θ = ${given}/${h}. Find cos 2θ.`,
        latex: `\\${givenName}\\theta = \\frac{${given}}{${h}}`,
        value,
        requireSimplified: true,
        summary: `cos 2θ = cos²θ − sin²θ, and with the Pythagorean identity that is ${giveSine ? '1 − 2sin²θ' : '2cos²θ − 1'} — one function only, which is why this is the useful form. It gives ${minus(toText(value))}.`,
        hint: 'cos 2θ has three equivalent forms. Choose the one written in the function you were given.',
        steps: [
          { label: 'Choose the form that matches what you know.', latex: formula },
          { label: 'Substitute.', latex: substituted },
          { label: 'Evaluate.', latex: toLatex(value) },
        ],
        traps: distinctFractionTraps(value, [
          // cos²θ + sin²θ instead of cos²θ − sin²θ, which is 1 for every angle.
          { value: fraction(1, 1), misconceptionId: 'addition-formula-sign-flipped' },
          // Doubling the value instead of the angle.
          { value: fraction(2 * given, h), misconceptionId: 'double-angle-taken-as-doubling' },
        ]),
      });
    },
  }),

  template({
    id: 'addition-formula-choice',
    conceptId: 'trig-addition-formulas',
    description: 'Choose the correct expansion, sign included.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const wantSine = rng.bool();
      const plus = rng.bool();
      const bracket = plus ? '+' : '-';
      const name = wantSine ? '\\sin' : '\\cos';
      const first = wantSine ? '\\sin A\\cos B' : '\\cos A\\cos B';
      const second = wantSine ? '\\cos A\\sin B' : '\\sin A\\sin B';
      const between = wantSine === plus ? '+' : '-';
      const flipped = between === '+' ? '-' : '+';
      /*
       * The other formula's pairing, not a reordering of this one's.
       * cos A sin B + sin A cos B is the same number as the correct answer,
       * because multiplication commutes — an option that grades as right while
       * being offered as wrong. Crossing the functions instead gives the
       * expansion of the *other* function, which is wrong and is a mistake
       * students actually make.
       */
      const crossed = wantSine ? '\\sin A\\sin B' : '\\sin A\\cos B';
      const crossedSecond = wantSine ? '\\cos A\\cos B' : '\\cos A\\sin B';

      return choiceExercise({
        prompt: 'Which of these is the correct expansion?',
        latex: `${name}(A ${bracket} B)`,
        ...options(
          [
            { latex: `${first} ${between} ${second}`, correct: true },
            { latex: `${first} ${flipped} ${second}`, misconceptionId: 'addition-formula-sign-flipped' },
            { latex: `${name} A ${bracket} ${name} B`, misconceptionId: 'trig-function-distributed-over-sum' },
            { latex: `${crossed} ${between} ${crossedSecond}` },
          ],
          rng.shuffle,
        ),
        summary: wantSine
          ? `Sine keeps the sign of the bracket: sin(A ${minus(bracket)} B) = sin A cos B ${minus(between)} cos A sin B. Every term mixes one sine with one cosine.`
          : `Cosine reverses the sign of the bracket: cos(A ${minus(bracket)} B) = cos A cos B ${minus(between)} sin A sin B. Check it at A = B = 90°, where cos 180° = −1.`,
        hint: 'Two things to settle: which functions pair up, and what the sign between the products is.',
      });
    },
  }),

  /* -------------------------------------------------------- trig equations */
  template({
    id: 'trig-second-solution',
    conceptId: 'trig-equations',
    description: 'Find the second solution in a revolution using the circle’s symmetry.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const useSine = rng.bool();
      const first = rng.pick([20, 25, 35, 40, 50, 55, 65, 70, 80]);
      const second = useSine ? 180 - first : 360 - first;
      const wrong = useSine ? 360 - first : 180 - first;

      return numericExercise({
        prompt: `One solution of ${useSine ? 'sin' : 'cos'} x = k is x = ${first}°. What is the other solution between 0° and 360°?`,
        unit: '°',
        value: second,
        summary: useSine
          ? `Sine is symmetric about the vertical axis, so the partner of ${first}° is 180° − ${first}° = ${second}°.`
          : `Cosine is symmetric about the horizontal axis, so the partner of ${first}° is 360° − ${first}° = ${second}°.`,
        hint: 'Draw the horizontal line across the circle. Where is the second crossing?',
        steps: [
          {
            label: useSine ? 'Sine: reflect in the vertical axis.' : 'Cosine: reflect in the horizontal axis.',
            latex: useSine ? `180^{\\circ} - ${first}^{\\circ}` : `360^{\\circ} - ${first}^{\\circ}`,
          },
          { label: 'Evaluate.', latex: `${second}^{\\circ}` },
        ],
        // Reflecting in the axis the other function uses.
        traps: [trap(wrong, 'reference-angle-from-wrong-axis')],
      });
    },
  }),

  template({
    id: 'trig-solution-count',
    conceptId: 'trig-equations',
    description: 'Count the solutions of a trigonometric equation over an interval.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const revolutions = rng.int(1, 4);
      const upper = 360 * revolutions;
      const useSine = rng.bool();
      const value = rng.pick([0.2, 0.3, 0.4, 0.6, 0.7, 0.8]);

      return numericExercise({
        prompt: `How many solutions does ${useSine ? 'sin' : 'cos'} x = ${value} have for 0° ≤ x ≤ ${upper}°?`,
        value: 2 * revolutions,
        summary: `A value of ${value} is reached twice in every revolution — once on each side of the axis of symmetry — and ${upper}° is ${revolutions} revolution${revolutions === 1 ? '' : 's'}, so there are ${2 * revolutions} solutions.`,
        hint: 'Two crossings per revolution. How many revolutions is the interval?',
        steps: [
          { label: 'Solutions per revolution.', latex: '2' },
          { label: 'Revolutions in the interval.', latex: `${upper} \\div 360 = ${revolutions}` },
          { label: 'Multiply.', latex: `${2 * revolutions}` },
        ],
        // One per revolution: the answer of a student who takes the inverse
        // function's single output as the whole story.
        traps: [trap(revolutions, 'trig-solution-single')],
      });
    },
  }),

  template({
    id: 'trig-general-solution',
    conceptId: 'trig-equations',
    description: 'Write the general solution, with the right period attached.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const useTangent = rng.bool();
      /* 60° is excluded: doubling it lands on 180° − 60°, which is the partner
       * solution, and the fourth option would then be correct as well. */
      const first = rng.pick([20, 25, 35, 40, 50, 55, 65, 70]);
      const period = useTangent ? 180 : 360;
      const partner = useTangent ? first + 180 : 180 - first;

      return choiceExercise({
        prompt: `Which expression gives *every* solution of ${useTangent ? 'tan' : 'sin'} x = k, given that x = ${first}° is one of them?`,
        ...options(
          [
            {
              label: useTangent ? `${first}° + 180°n` : `${first}° + 360°n, or ${partner}° + 360°n`,
              correct: true,
            },
            {
              label: useTangent ? `${first}° + 360°n` : `${first}° + 180°n, or ${partner}° + 180°n`,
              misconceptionId: 'general-solution-period-wrong',
            },
            { label: `${first}° only`, misconceptionId: 'trig-solution-single' },
            { label: `${2 * first}° + ${period}°n` },
          ],
          rng.shuffle,
        ),
        summary: useTangent
          ? `Tangent repeats every 180°, and it takes each value once per period — so one family, ${first}° + 180°n, covers everything.`
          : `Sine repeats every 360° and takes each value twice per revolution, so two families are needed: ${first}° and ${partner}°, each with 360°n added.`,
        hint: `How often does ${useTangent ? 'tangent' : 'sine'} repeat, and how many times per period does it hit a given value?`,
      });
    },
  }),

  /* ------------------------------------------------ the sine and cosine rules */
  template({
    id: 'triangle-rule-choice',
    conceptId: 'law-of-sines-cosines',
    description: 'Choose between the sine rule and the cosine rule from what is given.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const cases = [
        { given: 'all three sides', cosine: true, why: 'No angle is known at all, so no matched pair exists — rearrange the cosine rule to find one.' },
        { given: 'two sides and the angle between them', cosine: true, why: 'The known angle sits between the two known sides, so it is opposite the unknown one. That is exactly the cosine rule’s arrangement.' },
        { given: 'two angles and one side', cosine: false, why: 'The third angle is 180° minus the other two, so every angle is known and a side sits opposite one of them.' },
        { given: 'two sides and an angle opposite one of them', cosine: false, why: 'A side and its opposite angle are both known, which is the matched pair the sine rule needs.' },
      ];
      const chosen = rng.pick(cases);

      return choiceExercise({
        prompt: `A triangle has no right angle. You know ${chosen.given}. Which rule finds the missing measurement?`,
        ...options(
          [
            { label: chosen.cosine ? 'The cosine rule' : 'The sine rule', correct: true },
            {
              label: chosen.cosine ? 'The sine rule' : 'The cosine rule',
              misconceptionId: 'wrong-triangle-rule-chosen',
            },
            { label: 'Pythagoras’ theorem' },
            { label: 'Neither — the triangle is not determined' },
          ],
          rng.shuffle,
        ),
        summary: chosen.why,
        hint: 'The sine rule needs a side and the angle opposite it. Do you have that pair yet?',
      });
    },
  }),

  template({
    id: 'cosine-rule-side',
    conceptId: 'law-of-sines-cosines',
    description: 'Find the third side from two sides and the angle between them.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const a = rng.int(5, difficulty === 4 ? 14 : 9);
      const b = rng.int(a + 1, a + 8);
      /* Acute, so the misread route below still has a real square root. */
      const angle = rng.pick([30, 40, 50, 60, 70]);
      const cosine = Math.cos(radians(angle));
      const c = round1(Math.sqrt(a * a + b * b - 2 * a * b * cosine));
      const misread = round1(Math.sqrt((a * a + b * b - 2 * a * b) * cosine));

      return numericExercise({
        prompt: `In triangle ABC, a = ${a}, b = ${b} and C = ${angle}°. Find c, to 1 decimal place.`,
        figure: {
          kind: 'oblique-triangle',
          shape: 'acute',
          labels: { a: `${a}`, b: `${b}`, C: `${angle}°`, c: 'c' },
        },
        value: c,
        tolerance: within(0.05, c),
        summary: `c² = ${a}² + ${b}² − 2(${a})(${b})cos ${angle}° = ${round1(a * a + b * b - 2 * a * b * cosine)}, so c ≈ ${c}. Only the last term carries the cosine.`,
        hint: 'Two sides and the angle between them: the cosine rule. Work the correction term out on its own.',
        steps: [
          { label: 'Write the rule.', latex: 'c^{2} = a^{2} + b^{2} - 2ab\\cos C' },
          { label: 'Substitute.', latex: `c^{2} = ${a * a} + ${b * b} - ${2 * a * b}\\cos ${angle}^{\\circ}` },
          { label: 'The correction term alone.', latex: `${2 * a * b}\\cos ${angle}^{\\circ} \\approx ${round1(2 * a * b * cosine)}` },
          { label: 'Take the root.', latex: `c \\approx ${c}` },
        ],
        // Applying the cosine to the whole expression rather than the last term.
        traps: [trap(misread, 'cosine-rule-terms-not-separated')],
      });
    },
  }),

  template({
    id: 'sine-rule-side',
    conceptId: 'law-of-sines-cosines',
    description: 'Find a side from two angles and a side.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const A = rng.pick([35, 40, 45, 50, 55, 60]);
      const B = rng.pick([65, 70, 75, 80, 85]);
      const a = rng.int(6, 18);
      const b = round1((a * Math.sin(radians(B))) / Math.sin(radians(A)));
      const inverted = round1((a * Math.sin(radians(A))) / Math.sin(radians(B)));

      return numericExercise({
        prompt: `In triangle ABC, A = ${A}°, B = ${B}° and a = ${a}. Find b, to 1 decimal place.`,
        figure: {
          kind: 'oblique-triangle',
          shape: 'acute',
          labels: { A: `${A}°`, B: `${B}°`, a: `${a}`, b: 'b' },
        },
        value: b,
        tolerance: within(0.05, b),
        summary: `b / sin ${B}° = ${a} / sin ${A}°, so b = ${a} · sin ${B}° ÷ sin ${A}° ≈ ${b}. The larger angle faces the longer side, which is the check worth making.`,
        hint: 'Put the side you want on top: b = a · sin B ÷ sin A.',
        steps: [
          { label: 'Write the rule with the wanted side on top.', latex: '\\frac{b}{\\sin B} = \\frac{a}{\\sin A}' },
          { label: 'Rearrange.', latex: `b = \\frac{${a}\\sin ${B}^{\\circ}}{\\sin ${A}^{\\circ}}` },
          { label: 'Evaluate.', latex: `b \\approx ${b}` },
        ],
        // The ratio upside down — the sines swapped between top and bottom.
        traps: [trap(inverted, 'wrong-triangle-rule-chosen')],
      });
    },
  }),

  template({
    id: 'ambiguous-case-obtuse',
    conceptId: 'law-of-sines-cosines',
    description: 'Give the obtuse angle in a triangle that admits two shapes.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      /*
       * Chosen so that b·sin A < a < b, which is exactly the band where two
       * triangles fit the same three measurements. Outside it the item would
       * have one answer and would diagnose nothing.
       */
      const A = rng.pick([25, 30, 35, 40]);
      const b = rng.int(9, 16);
      const lower = b * Math.sin(radians(A));
      const a = round1(lower + (b - lower) * rng.pick([0.4, 0.5, 0.6, 0.7]));
      const acute = Math.round((Math.asin((b * Math.sin(radians(A))) / a) * 180) / Math.PI);
      const obtuse = 180 - acute;

      return numericExercise({
        prompt: `In triangle ABC, A = ${A}°, a = ${a} and b = ${b}. Two different triangles fit these measurements. Find the obtuse angle B, to the nearest degree.`,
        figure: {
          kind: 'oblique-triangle',
          shape: 'acute',
          showAmbiguous: true,
          labels: { A: `${A}°`, a: `${a}`, b: `${b}` },
        },
        unit: '°',
        value: obtuse,
        tolerance: within(0.6, obtuse),
        summary: `sin B = ${b} · sin ${A}° ÷ ${a}, which gives B ≈ ${acute}° — but sin ${acute}° and sin ${obtuse}° are the same number, so B ≈ ${obtuse}° fits the same three measurements.`,
        hint: 'An inverse sine returns the acute angle. What other angle has the same sine?',
        steps: [
          { label: 'Sine rule, rearranged for the angle.', latex: `\\sin B = \\frac{${b}\\sin ${A}^{\\circ}}{${a}}` },
          { label: 'The inverse sine gives the acute option.', latex: `B \\approx ${acute}^{\\circ}` },
          { label: 'Its supplement has the same sine.', latex: `180^{\\circ} - ${acute}^{\\circ} = ${obtuse}^{\\circ}` },
        ],
        // The acute angle: correct for the other triangle, and the only one an
        // inverse sine will ever report.
        traps: [trap(acute, 'ambiguous-case-missed')],
      });
    },
  }),

  /* -------------------------------------------------------- conic sections */
  template({
    id: 'circle-radius-from-equation',
    conceptId: 'conic-sections',
    description: 'Read the radius off a circle’s equation.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const r = rng.int(2, difficulty === 3 ? 12 : 8);
      const squared = r * r;

      return numericExercise({
        prompt: `What is the radius of the circle x² + y² = ${squared}?`,
        latex: `x^{2} + y^{2} = ${squared}`,
        value: r,
        summary: `The equation gives the radius squared, so the radius is √${squared} = ${r}.`,
        hint: 'The right-hand side is r², not r.',
        steps: [
          { label: 'Compare with the standard form.', latex: `r^{2} = ${squared}` },
          { label: 'Take the root.', latex: `r = ${r}` },
        ],
        // The square left untaken.
        traps: [trap(squared, 'conic-radius-not-rooted')],
      });
    },
  }),

  template({
    id: 'circle-centre-from-equation',
    conceptId: 'conic-sections',
    description: 'Read the centre off a translated circle’s equation.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const h = rng.int(1, 9) * (rng.bool() ? 1 : -1);
      const k = rng.int(1, 9) * (rng.bool() ? 1 : -1);
      const r = rng.int(2, 9);
      const wantX = rng.bool();
      const wanted = wantX ? h : k;
      const written = wantX ? -h : -k;

      return numericExercise({
        prompt: `What is the ${wantX ? 'x' : 'y'}-coordinate of the centre of this circle?`,
        latex: `(x ${h < 0 ? '+' : '-'} ${Math.abs(h)})^{2} + (y ${k < 0 ? '+' : '-'} ${Math.abs(k)})^{2} = ${r * r}`,
        value: wanted,
        summary: `The standard form is (x − h)² + (y − k)², so the centre is (${minus(h)}, ${minus(k)}) — the values that make each bracket zero.`,
        hint: 'Which value makes the bracket zero? That is the coordinate.',
        steps: [
          { label: 'Compare with the standard form.', latex: '(x - h)^{2} + (y - k)^{2} = r^{2}' },
          { label: 'Set the bracket to zero.', latex: `${wantX ? 'x' : 'y'} = ${wanted}` },
        ],
        // Reading the number as printed rather than as subtracted.
        traps: [trap(written, 'conic-centre-sign-inverted')],
      });
    },
  }),

  template({
    id: 'ellipse-long-direction',
    conceptId: 'conic-sections',
    description: 'Decide which way an ellipse is stretched.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const small = rng.int(2, 6);
      const large = rng.int(small + 2, small + 9);
      const wideX = rng.bool();
      const underX = wideX ? large : small;
      const underY = wideX ? small : large;

      return choiceExercise({
        prompt: 'Is this ellipse wider than it is tall, or taller than it is wide?',
        latex: `\\frac{x^{2}}{${underX * underX}} + \\frac{y^{2}}{${underY * underY}} = 1`,
        ...options(
          [
            { label: wideX ? 'Wider than it is tall' : 'Taller than it is wide', correct: true },
            {
              label: wideX ? 'Taller than it is wide' : 'Wider than it is tall',
              misconceptionId: 'ellipse-axes-swapped',
            },
            { label: 'Neither — it is a circle' },
            { label: 'It is a hyperbola, not an ellipse' },
          ],
          rng.shuffle,
        ),
        summary: `The larger denominator is ${large * large}, under ${wideX ? 'x²' : 'y²'}, so the ellipse reaches ${large} in the ${wideX ? 'horizontal' : 'vertical'} direction and only ${small} in the other.`,
        hint: 'The larger denominator marks the long direction, wherever it is written.',
      });
    },
  }),

  template({
    id: 'classify-conic',
    conceptId: 'conic-sections',
    description: 'Name the conic from the shape of its equation.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(2, 7);
      const b = rng.int(2, 7);
      const family = rng.pick(['circle', 'ellipse', 'parabola', 'hyperbola'] as const);
      const shown =
        family === 'circle'
          ? `x^{2} + y^{2} = ${a * a}`
          : family === 'ellipse'
            ? `\\frac{x^{2}}{${a * a}} + \\frac{y^{2}}{${b * b + 4}} = 1`
            : family === 'parabola'
              ? `y = ${a}x^{2} + ${b}`
              : `\\frac{x^{2}}{${a * a}} - \\frac{y^{2}}{${b * b + 4}} = 1`;
      const why = {
        circle: 'Both squared terms are present, added, and have the same coefficient — that is a circle.',
        ellipse: 'Both squared terms are present and added, but with different denominators, so it is an ellipse.',
        parabola: 'Only one variable is squared, which makes it a parabola.',
        hyperbola: 'Both squared terms are present but subtracted. Opposite signs mean a hyperbola.',
      }[family];

      return choiceExercise({
        prompt: 'Which curve is this?',
        latex: shown,
        ...options(
          [
            { label: titled(family), correct: true },
            ...(['circle', 'ellipse', 'parabola', 'hyperbola'] as const)
              .filter((name) => name !== family)
              .map((name) => ({ label: titled(name) })),
          ],
          rng.shuffle,
        ),
        summary: why,
        hint: 'Look at the two squared coefficients: equal, unequal, opposite signs, or only one of them.',
      });
    },
  }),

  /* ---------------------------------------------------------------- vectors */
  template({
    id: 'vector-magnitude',
    conceptId: 'vectors',
    description: 'Find the length of a vector from its components.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const [x, y, h] = rng.pick(difficulty === 3 ? TRIPLES : TRIPLES.slice(0, 3));
      const acrossFirst = rng.bool();
      const across = acrossFirst ? x : y;
      const up = acrossFirst ? y : x;

      return numericExercise({
        prompt: `What is the magnitude of the vector (${across}, ${up})?`,
        latex: `\\begin{pmatrix} ${across} \\\\ ${up} \\end{pmatrix}`,
        figure: { kind: 'vector-diagram', min: -1, max: h, showComponents: true, vectors: [{ x: across, y: up, label: 'v' }] },
        value: h,
        summary: `The across-part and the up-part meet at a right angle, so the length is √(${across}² + ${up}²) = √${h * h} = ${h}.`,
        hint: 'The two components are perpendicular. Which theorem gives the third side?',
        steps: [
          { label: 'Square each component.', latex: `${across * across} + ${up * up} = ${h * h}` },
          { label: 'Take the root.', latex: `\\sqrt{${h * h}} = ${h}` },
        ],
        // Adding the components, as though the journey were in a straight line.
        traps: [trap(across + up, 'vector-magnitude-as-sum')],
      });
    },
  }),

  template({
    id: 'vector-add-component',
    conceptId: 'vectors',
    description: 'Add two vectors component by component.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const limit = difficulty === 3 ? 11 : 7;
      const ax = rng.int(-limit, limit);
      const ay = rng.int(-limit, limit);
      const bx = rng.int(-limit, limit);
      const by = rng.int(-limit, limit);
      const sumX = ax + bx;
      const sumY = ay + by;
      const wantX = rng.bool();

      return numericExercise({
        prompt: `Add these vectors. What is the ${wantX ? 'across' : 'up'}-component of the answer?`,
        latex: `\\begin{pmatrix} ${ax} \\\\ ${ay} \\end{pmatrix} + \\begin{pmatrix} ${bx} \\\\ ${by} \\end{pmatrix}`,
        value: wantX ? sumX : sumY,
        summary: `The two directions never interfere, so they are added separately: the answer is (${minus(sumX)}, ${minus(sumY)}).`,
        hint: 'Across with across, up with up. The two directions are independent.',
        steps: [
          { label: 'Add the across-parts.', latex: `${ax} + (${bx}) = ${sumX}` },
          { label: 'Add the up-parts.', latex: `${ay} + (${by}) = ${sumY}` },
        ],
        // Reading off the other component — right arithmetic, wrong axis.
        traps: [...(sumX === sumY ? [] : [trap(wantX ? sumY : sumX, 'vector-components-swapped')])],
      });
    },
  }),

  template({
    id: 'vector-resultant-perpendicular',
    conceptId: 'vectors',
    description: 'Combine two perpendicular vectors.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const [x, y, h] = rng.pick(TRIPLES);
      const east = rng.bool() ? x : y;
      const north = east === x ? y : x;

      return numericExercise({
        prompt: `A boat steers due north at ${north} km/h. A current carries it due east at ${east} km/h. What is its speed over the ground?`,
        unit: 'km/h',
        figure: {
          kind: 'vector-diagram',
          min: -1,
          max: h + 1,
          vectors: [
            { x: 0, y: north, label: 'boat' },
            { x: east, y: 0, label: 'current', from: { x: 0, y: north } },
            { x: east, y: north, label: 'result', dashed: true },
          ],
        },
        value: h,
        summary: `North and east are at right angles, so the speeds do not simply add: √(${east}² + ${north}²) = ${h} km/h.`,
        hint: 'The two motions are perpendicular. Draw them nose to tail and look at the triangle.',
        steps: [
          { label: 'Write the resultant in components.', latex: `\\begin{pmatrix} ${east} \\\\ ${north} \\end{pmatrix}` },
          { label: 'Take its magnitude.', latex: `\\sqrt{${east * east} + ${north * north}} = ${h}` },
        ],
        // Adding the two speeds, as though direction did not matter.
        traps: [trap(east + north, 'vector-direction-ignored')],
      });
    },
  }),

  template({
    id: 'vector-from-magnitude-direction',
    conceptId: 'vectors',
    description: 'Resolve a vector into components from its length and angle.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const magnitude = rng.int(6, 30);
      const angle = rng.pick([20, 25, 35, 40, 55, 65, 70, 80]);
      const across = round1(magnitude * Math.cos(radians(angle)));
      const up = round1(magnitude * Math.sin(radians(angle)));

      return numericExercise({
        prompt: `A force of ${magnitude} N acts at ${angle}° above the horizontal. What is its horizontal component, to 1 decimal place?`,
        unit: 'N',
        value: across,
        tolerance: within(0.05, across),
        summary: `The horizontal component is ${magnitude} cos ${angle}° ≈ ${across} N. Cosine goes with the across-direction, as it does everywhere on the unit circle.`,
        hint: 'Cosine is across and sine is up — the same convention as the unit circle.',
        steps: [
          { label: 'Write the components.', latex: `(|\\mathbf{v}|\\cos ${angle}^{\\circ}, \\; |\\mathbf{v}|\\sin ${angle}^{\\circ})` },
          { label: 'Take the horizontal one.', latex: `${magnitude}\\cos ${angle}^{\\circ} \\approx ${across}` },
        ],
        // The vertical component: the right calculation on the wrong axis.
        traps: [...(Math.abs(across - up) < 0.2 ? [] : [trap(up, 'vector-components-swapped')])],
      });
    },
  }),

  /* ----------------------------------------------------------------- limits */
  template({
    id: 'limit-at-a-hole',
    conceptId: 'limits',
    description: 'Decide what a limit is where the function is undefined.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a = rng.int(2, 9);
      const limit = 2 * a;

      return choiceExercise({
        prompt: `The function below is undefined at x = ${a}. What is its limit as x approaches ${a}?`,
        latex: `f(x) = \\frac{x^{2} - ${a * a}}{x - ${a}}`,
        ...options(
          [
            { latex: `${limit}`, correct: true },
            { label: 'The limit does not exist', misconceptionId: 'limit-taken-as-value' },
            { latex: '0', misconceptionId: 'indeterminate-form-called-undefined' },
            { latex: `${a}` },
          ],
          rng.shuffle,
        ),
        summary: `The numerator factorises as (x − ${a})(x + ${a}), and away from x = ${a} the function is simply x + ${a}. So it heads towards ${a} + ${a} = ${limit}, whatever happens at the point itself.`,
        hint: 'A limit asks where the function is heading, and never about the point itself.',
      });
    },
  }),

  template({
    id: 'limit-cancel-factor',
    conceptId: 'limits',
    description: 'Resolve a 0/0 form by cancelling the common factor.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const r = rng.int(1, difficulty === 4 ? 9 : 5);
      let s = rng.int(-8, 8);
      // s = r would cancel to a repeated factor, and s making r − s zero would
      // put the answer on top of the 0/0 trap below.
      if (s === r) s = r + 1;
      const sum = r + s;
      const product = r * s;
      const answer = r - s;
      const numerator = expr('x^{2}', signedTerm(-sum, 'x'), signedConstant(product));
      const cancelled = expr('x', signedConstant(-s));

      return numericExercise({
        prompt: `Evaluate the limit as x approaches ${r}.`,
        latex: `\\lim_{x \\to ${r}} \\frac{${numerator}}{x - ${r}}`,
        value: answer,
        summary: `The numerator is (x − ${r})(${minus(cancelled)}). Cancelling the shared factor leaves ${minus(cancelled)}, and substituting gives ${minus(answer)}.`,
        hint: 'Substituting gives 0/0. That is a signal, not an answer — factorise the top.',
        steps: [
          { label: 'Substitute first.', latex: '\\frac{0}{0}', note: 'A shared factor, not a dead end.' },
          { label: 'Factorise the numerator.', latex: `\\frac{(x - ${r})(${cancelled})}{x - ${r}}` },
          { label: `Cancel — legal, because x never equals ${r} here.`, latex: cancelled },
          { label: 'Now substitute.', latex: `${r} - ${s} = ${answer}` },
        ],
        // Reading 0/0 as the value of the limit.
        traps: [...(answer === 0 ? [] : [trap(0, 'indeterminate-form-called-undefined')])],
      });
    },
  }),

  template({
    id: 'limit-at-infinity',
    conceptId: 'limits',
    description: 'Find the limit of a rational function far from the origin.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const top = rng.int(2, 12);
      const bottom = rng.int(2, 12);
      const c = rng.int(1, 9);
      const d = rng.int(1, 9);
      const numerator = expr(leadingTerm(top, 'x^{2}'), signedTerm(c, 'x'));
      const denominator = expr(leadingTerm(bottom, 'x^{2}'), signedConstant(d));
      const settled = fraction(top, bottom);

      return fractionExercise({
        prompt: 'What value does this function approach as x grows without bound?',
        latex: `\\frac{${numerator}}{${denominator}}`,
        value: settled,
        requireSimplified: true,
        summary: `Far out, the x² terms dwarf everything else, so the ratio settles at ${top}/${bottom}${toText(settled) === `${top}/${bottom}` ? '' : ` = ${toText(settled)}`}.`,
        hint: 'At x = 1000, which terms are worth anything?',
        steps: [
          { label: 'Divide top and bottom by x².', latex: `\\frac{${top} + ${c}/x}{${bottom} + ${d}/x^{2}}` },
          { label: 'The 1/x terms vanish.', latex: `\\frac{${top}}{${bottom}}` },
        ],
        // Reading the limit off the terms that stop mattering.
        traps: [...(c * bottom === top * d ? [] : [fractionTrap(fraction(c, d), 'end-behaviour-from-wrong-term')])],
      });
    },
  }),

  /* ------------------------------------------------------ polar coordinates */
  template({
    id: 'polar-to-cartesian',
    conceptId: 'polar-coordinates',
    description: 'Convert a polar pair to the coordinate that comes out exact.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      /*
       * Only the coordinate with a rational cosine or sine is asked for. At
       * 120° the across-coordinate is −r/2 and the up-coordinate is r√3/2,
       * which no student can type and no grader should demand — so the item
       * asks for the first and says nothing about the second.
       */
      const onAxis = [
        { degrees: 0, axis: 'x', sign: 1, ratio: 'cos 0° = 1', factor: '1' },
        { degrees: 90, axis: 'y', sign: 1, ratio: 'sin 90° = 1', factor: '1' },
        { degrees: 180, axis: 'x', sign: -1, ratio: 'cos 180° = −1', factor: '−1' },
        { degrees: 270, axis: 'y', sign: -1, ratio: 'sin 270° = −1', factor: '−1' },
      ] as const;
      const halved = [
        { degrees: 60, axis: 'x', sign: 1, ratio: 'cos 60° = 1/2', factor: '1/2' },
        { degrees: 120, axis: 'x', sign: -1, ratio: 'cos 120° = −1/2', factor: '−1/2' },
        { degrees: 240, axis: 'x', sign: -1, ratio: 'cos 240° = −1/2', factor: '−1/2' },
        { degrees: 300, axis: 'x', sign: 1, ratio: 'cos 300° = 1/2', factor: '1/2' },
        { degrees: 30, axis: 'y', sign: 1, ratio: 'sin 30° = 1/2', factor: '1/2' },
        { degrees: 150, axis: 'y', sign: 1, ratio: 'sin 150° = 1/2', factor: '1/2' },
        { degrees: 210, axis: 'y', sign: -1, ratio: 'sin 210° = −1/2', factor: '−1/2' },
        { degrees: 330, axis: 'y', sign: -1, ratio: 'sin 330° = −1/2', factor: '−1/2' },
      ] as const;

      const entry = rng.pick(difficulty === 2 ? onAxis : [...onAxis, ...halved]);
      const r = 2 * rng.int(1, 6);
      const isAxis = entry.degrees % 90 === 0;
      const magnitude = isAxis ? r : r / 2;
      const value = entry.sign * magnitude;
      const fn = entry.axis === 'x' ? 'cos' : 'sin';

      return numericExercise({
        prompt: `The point (${r}, ${entry.degrees}°) is given in polar coordinates. What is its ${entry.axis}-coordinate?`,
        latex: entry.axis === 'x' ? `x = r\\cos\\theta` : `y = r\\sin\\theta`,
        value,
        summary: `${entry.ratio}, so ${entry.axis} = ${r} × ${entry.factor} = ${minus(value)}.`,
        hint: `The across-coordinate uses the cosine and the up-coordinate uses the sine. Which one is ${entry.axis}?`,
        steps: [
          { label: 'Write the conversion.', latex: entry.axis === 'x' ? 'x = r\\cos\\theta' : 'y = r\\sin\\theta' },
          { label: 'Substitute.', latex: `${r}\\${fn} ${entry.degrees}^{\\circ}` },
          { label: 'Evaluate.', latex: `${value}` },
        ],
        traps: [
          // The other function used: at a quadrantal angle it returns zero.
          ...(isAxis ? [trap(0, 'sine-cosine-coordinates-swapped')] : []),
          // The size taken from the ratio with the quadrant's sign discarded.
          ...(entry.sign === -1 ? [trap(magnitude, 'quadrant-sign-ignored')] : []),
        ],
      });
    },
  }),

  template({
    id: 'cartesian-to-polar-r',
    conceptId: 'polar-coordinates',
    description: 'Find r from a point given in Cartesian coordinates.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const [o, a, h] = rng.pick(difficulty === 3 ? TRIPLES : TRIPLES.slice(0, 4));
      const across = (rng.bool() ? 1 : -1) * (rng.bool() ? o : a);
      const up = (rng.bool() ? 1 : -1) * (Math.abs(across) === o ? a : o);

      return numericExercise({
        prompt: `Find r for the point (${minus(across)}, ${minus(up)}).`,
        latex: `r = \\sqrt{x^{2} + y^{2}}`,
        value: h,
        summary: `r is the distance from the origin: √(${across * across} + ${up * up}) = √${h * h} = ${h}. Squaring removes the signs, so r never depends on the quadrant.`,
        hint: 'This is Pythagoras on the right triangle under the point — the same calculation as the magnitude of a vector.',
        steps: [
          { label: 'Square both coordinates.', latex: `(${across})^{2} + (${up})^{2} = ${across * across} + ${up * up}` },
          { label: 'Add.', latex: `${h * h}` },
          { label: 'Take the square root.', latex: `${h}` },
        ],
        traps: [
          // The two legs added, which is not how a right triangle works.
          trap(Math.abs(across) + Math.abs(up), 'vector-magnitude-as-sum'),
          // The square root left untaken.
          trap(h * h, 'conic-radius-not-rooted'),
        ],
      });
    },
  }),

  template({
    id: 'cartesian-to-polar-angle',
    conceptId: 'polar-coordinates',
    description: 'Find θ for a point, with the quadrant settled by hand.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const size = rng.int(1, 7);
      const diagonals = [
        { x: 1, y: 1, degrees: 45, where: 'the first quadrant' },
        { x: -1, y: 1, degrees: 135, where: 'the second quadrant' },
        { x: -1, y: -1, degrees: 225, where: 'the third quadrant' },
        { x: 1, y: -1, degrees: 315, where: 'the fourth quadrant' },
      ] as const;
      const axes = [
        { x: 1, y: 0, degrees: 0, where: 'the positive x-axis' },
        { x: 0, y: 1, degrees: 90, where: 'the positive y-axis' },
        { x: -1, y: 0, degrees: 180, where: 'the negative x-axis' },
        { x: 0, y: -1, degrees: 270, where: 'the negative y-axis' },
      ] as const;

      const entry = rng.pick(difficulty === 4 ? [...diagonals, ...axes] : axes);
      const across = entry.x * size;
      const up = entry.y * size;
      const isDiagonal = entry.x !== 0 && entry.y !== 0;
      // Swapping the coordinates turns each axis case into its neighbour.
      const swapped = [...axes].find((option) => option.x === entry.y && option.y === entry.x)?.degrees;

      return numericExercise({
        prompt: `The point (${minus(across)}, ${minus(up)}) is given in Cartesian coordinates. What is θ, in degrees, with 0° ≤ θ < 360°?`,
        unit: '°',
        value: entry.degrees,
        summary: isDiagonal
          ? `The two coordinates are the same size, so the acute angle to the x-axis is 45°. The point lies in ${entry.where}, which puts θ at ${entry.degrees}°.`
          : `The point lies on ${entry.where}, so θ = ${entry.degrees}° — no inverse tangent needed.`,
        hint: 'Find the acute angle to the x-axis first, then decide the quadrant by looking at the signs.',
        steps: isDiagonal
          ? [
              { label: 'The acute angle to the x-axis.', latex: '\\tan^{-1}(1) = 45^{\\circ}' },
              { label: 'Locate the point.', latex: `(${across},\\; ${up}) \\text{ lies in } ${entry.degrees === 45 ? 'Q1' : entry.degrees === 135 ? 'Q2' : entry.degrees === 225 ? 'Q3' : 'Q4'}` },
              { label: 'Measure round from the positive x-axis.', latex: `${entry.degrees}^{\\circ}` },
            ]
          : [
              { label: 'Plot the point.', latex: `(${across},\\; ${up})` },
              { label: 'Read the direction.', latex: `${entry.degrees}^{\\circ}` },
            ],
        traps: [
          // arctan of the two sizes, with the quadrant never looked at.
          ...(isDiagonal && entry.degrees !== 45 ? [trap(45, 'polar-angle-from-arctan-alone')] : []),
          // The coordinates read the wrong way round.
          ...(!isDiagonal && swapped !== undefined ? [trap(swapped, 'vector-components-swapped')] : []),
        ],
      });
    },
  }),

  template({
    id: 'polar-same-point',
    conceptId: 'polar-coordinates',
    description: 'Recognise a second polar name for one point.',
    difficulties: [3, 4, 5],
    build: ({ rng }) => {
      const r = rng.int(2, 9);
      const theta = rng.pick([30, 45, 60, 120, 135, 150, 210, 225, 240, 300, 315, 330]);

      return choiceExercise({
        prompt: `Which of these names the same point as (${r}, ${theta}°)?`,
        ...options(
          [
            { label: `(${r}, ${theta + 360}°)`, correct: true },
            { label: `(${theta}, ${r}°)`, misconceptionId: 'polar-coordinates-swapped' },
            { label: `(${r}, ${minus(-theta)}°)` },
            { label: `(${minus(-r)}, ${theta}°)` },
          ],
          rng.shuffle,
        ),
        summary: `A whole extra turn changes nothing about where you end up, so ${theta}° and ${theta + 360}° are the same direction. Negating θ reflects the point in the x-axis, and a negative r sends it out the opposite way — both land somewhere else.`,
        hint: 'Which change leaves both the distance and the direction alone?',
      });
    },
  }),

  template({
    id: 'polar-equation-shape',
    conceptId: 'polar-coordinates',
    description: 'Say what a one-variable polar equation describes.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const fixRadius = rng.bool();
      const r = rng.int(2, 9);
      const theta = rng.pick([30, 45, 60, 120, 135, 150]);

      if (fixRadius) {
        return choiceExercise({
          prompt: `In polar coordinates, what does this equation describe?`,
          latex: `r = ${r}`,
          ...options(
            [
              { label: `The circle of radius ${r} centred at the origin`, correct: true },
              { label: `The ray from the origin at ${r}° to the x-axis`, misconceptionId: 'polar-coordinates-swapped' },
              { label: `The vertical line x = ${r}` },
              { label: `The single point (${r}, 0°)` },
            ],
            rng.shuffle,
          ),
          summary: `The equation fixes the distance and says nothing about the direction, so every direction is allowed: all the points ${r} units from the origin, which is a circle.`,
          hint: 'Which of the two coordinates is pinned down, and which is free to be anything?',
        });
      }

      return choiceExercise({
        prompt: `In polar coordinates, what does this equation describe?`,
        latex: `\\theta = ${theta}^{\\circ}`,
        ...options(
          [
            { label: `The ray from the origin at ${theta}° to the x-axis`, correct: true },
            { label: `The circle of radius ${theta} centred at the origin`, misconceptionId: 'polar-coordinates-swapped' },
            { label: `The horizontal line y = ${theta}` },
            { label: `The single point ${theta} units along the x-axis` },
          ],
          rng.shuffle,
        ),
        summary: `The direction is fixed and the distance is free, so the graph is every point in that one direction — a ray from the origin. Allowing r to be negative as well extends it backwards into a full line.`,
        hint: 'Which of the two coordinates is pinned down, and which is free to be anything?',
      });
    },
  }),

  /* --------------------------------------------------------- rate of change */
  template({
    id: 'rate-from-two-readings',
    conceptId: 'rates-of-change',
    description: 'Find an average rate of change from two readings.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      /*
       * The rate is restricted to values whose reciprocal terminates, so that
       * the upside-down quotient Δx/Δy is a number a student could actually
       * type. 1/3 is 0.333…, which no one writes and no grader would see — and
       * a trap nobody can hit diagnoses nothing.
       */
      const rate = rng.pick([2, 4, 5, 8, 10]);
      const contexts = [
        { subject: 'A tank', measure: 'holds', quantity: 'litres', unit: 'litres per minute', clock: 'minute' },
        { subject: 'A seedling', measure: 'is', quantity: 'cm tall', unit: 'cm per day', clock: 'day' },
        { subject: 'A printer', measure: 'has produced', quantity: 'pages', unit: 'pages per minute', clock: 'minute' },
        { subject: 'A savings account', measure: 'holds', quantity: 'pounds', unit: 'pounds per week', clock: 'week' },
      ];
      const context = rng.pick(contexts);
      const start = rng.int(1, 4);
      const span = rng.int(2, 6);
      const startValue = rng.int(4, 20);
      const endValue = startValue + rate * span;
      /* "after 1 minutes" is the sort of thing that makes content feel
       * generated, and the reading at t = 1 is a perfectly good reading. */
      const clock = (n: number): string => `${n} ${context.clock}${n === 1 ? '' : 's'}`;

      return numericExercise({
        prompt: `${context.subject} ${context.measure} ${startValue} ${context.quantity} after ${clock(start)}, and ${endValue} ${context.quantity} after ${clock(start + span)}. What is the average rate of change?`,
        unit: context.unit,
        value: rate,
        summary: `The output rose by ${endValue} − ${startValue} = ${rate * span} over ${clock(span)}, and a rate is the change per unit of input: ${rate * span} ÷ ${span} = ${rate}.`,
        hint: 'A change is not a rate. Divide by how far the input moved.',
        steps: [
          { label: 'Change in output.', latex: `${endValue} - ${startValue} = ${rate * span}` },
          { label: 'Change in input.', latex: `${start + span} - ${start} = ${span}` },
          { label: 'Divide the first by the second.', latex: `\\frac{${rate * span}}{${span}} = ${rate}` },
        ],
        traps: [
          // The change reported without the division.
          trap(rate * span, 'difference-quotient-not-divided'),
          // The quotient the other way up: input per unit of output.
          trap(1 / rate, 'rate-of-change-inverted'),
        ],
      });
    },
  }),

  template({
    id: 'average-rate-quadratic',
    conceptId: 'rates-of-change',
    description: 'Find the average rate of change of a quadratic over an interval.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const a = rng.int(1, difficulty === 4 ? 3 : 2);
      const b = rng.int(-4, 4);
      const c = rng.int(-5, 5);
      const p = rng.int(-3, 2);
      // A span of at least 2, so the undivided change cannot equal the rate.
      const q = p + rng.int(2, 4);
      const rate = a * (p + q) + b;
      // A rate of zero would make the change zero too, and the trap below would
      // be the answer. Nudge the linear coefficient rather than reroll.
      const shift = rate === 0 ? 1 : 0;
      const linear = b + shift;
      const answer = a * (p + q) + linear;
      const span = q - p;
      const change = answer * span;
      const at = (x: number): number => a * x * x + linear * x + c;
      const quadratic = a === 1 ? 'x^{2}' : `${a}x^{2}`;

      /* Brackets only where a minus sign would otherwise run into the one
       * before it: `7 - (-2)` needs them and `7 - (5)` is noise. */
      const braced = (value: number): string => (value < 0 ? `(${value})` : `${value}`);

      return numericExercise({
        prompt: `Find the average rate of change of f from x = ${minus(p)} to x = ${minus(q)}.`,
        latex: `f(x) = ${expr(quadratic, signedTerm(linear, 'x'), signedConstant(c))}`,
        value: answer,
        summary: `f(${minus(q)}) = ${minus(at(q))} and f(${minus(p)}) = ${minus(at(p))}, a change of ${minus(change)} over an input change of ${span}. The average rate is ${minus(change)} ÷ ${span} = ${minus(answer)}.`,
        hint: 'Average rate of change is (f(b) − f(a)) / (b − a) — the slope of the line joining the two points.',
        steps: [
          { label: 'Evaluate at both ends.', latex: `f(${p}) = ${at(p)}, \\quad f(${q}) = ${at(q)}` },
          { label: 'Change in output over change in input.', latex: `\\frac{${at(q)} - ${braced(at(p))}}{${q} - ${braced(p)}}` },
          { label: 'Evaluate.', latex: `\\frac{${change}}{${span}} = ${answer}` },
        ],
        traps: [
          // The change in output, with the division left out.
          trap(change, 'difference-quotient-not-divided'),
          // The quotient upside down, where that is a number one could type.
          ...([2, 4, 5, 8, 10, -2, -4, -5, -8, -10].includes(answer)
            ? [trap(1 / answer, 'rate-of-change-inverted')]
            : []),
        ],
      });
    },
  }),

  template({
    id: 'rate-average-or-instant',
    conceptId: 'rates-of-change',
    description: 'Tell what an average rate does and does not justify.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const speed = rng.pick([40, 50, 60, 80, 90]);
      const hours = rng.int(2, 5);
      const distance = speed * hours;
      const vehicles = [
        { name: 'A car', verb: 'travels' },
        { name: 'A train', verb: 'covers' },
        { name: 'A cyclist', verb: 'rides' },
      ];
      const vehicle = rng.pick(vehicles);

      return choiceExercise({
        prompt: `${vehicle.name} ${vehicle.verb} ${distance} km in ${hours} hours. Which statement does that justify?`,
        ...options(
          [
            { label: `Its average speed over the journey was ${speed} km/h`, correct: true },
            {
              label: `It was travelling at ${speed} km/h at every moment of the journey`,
              misconceptionId: 'average-rate-taken-as-instantaneous',
            },
            { label: `It travelled ${speed} km`, misconceptionId: 'difference-quotient-not-divided' },
            { label: `Its speed increased by ${speed} km/h during the journey` },
          ],
          rng.shuffle,
        ),
        summary: `${distance} ÷ ${hours} = ${speed}, and that is an average across the whole journey. It says nothing about any particular instant — the journey may have included a stop and an overtake and never touched ${speed} km/h at all.`,
        hint: 'An average is computed from two readings, and describes what happened between them — not what happened at any one moment.',
      });
    },
  }),

  template({
    id: 'instantaneous-rate-limit',
    conceptId: 'rates-of-change',
    description: 'Take the limit of a difference quotient as h shrinks to nothing.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const c = rng.int(1, 4);
      const a = rng.int(1, 8);
      const quotient = expr(`${2 * c * a}`, signedTerm(c, 'h'));
      const curve = c === 1 ? 'x^{2}' : `${c}x^{2}`;

      return numericExercise({
        prompt: `For f(x) = ${c === 1 ? 'x²' : `${c}x²`}, the average rate of change from x = ${a} to x = ${a} + h simplifies to ${quotient}. What is the rate of change at x = ${a} exactly?`,
        latex: `\\lim_{h \\to 0}\\left(${quotient}\\right)`,
        value: 2 * c * a,
        summary: `As h shrinks towards nothing the ${c === 1 ? 'h' : `${c}h`} term vanishes and the quotient settles at ${2 * c * a}. That is the rate at the single point x = ${a}, not an average over any interval.`,
        hint: 'The quotient is a function of h. What does it head towards as h shrinks to nothing?',
        steps: [
          { label: 'Write the difference quotient.', latex: `\\frac{f(${a} + h) - f(${a})}{h} = ${quotient}` },
          { label: 'Let h shrink to nothing.', latex: `\\lim_{h \\to 0}\\left(${quotient}\\right)` },
          { label: 'The h term vanishes.', latex: `${2 * c * a}`, note: `The curve is f(x) = ${curve}, and this is its rate at x = ${a}.` },
        ],
        traps: [
          // h = 1: the average over a unit interval, reported as the instant.
          trap(2 * c * a + c, 'average-rate-taken-as-instantaneous'),
        ],
      });
    },
  }),

  template({
    id: 'difference-quotient-simplify',
    conceptId: 'rates-of-change',
    description: 'Simplify a difference quotient before the limit is taken.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const c = rng.pick([1, 2]);
      let b = rng.int(-5, 5);
      if (b === 0) b = 3;
      const quadratic = c === 1 ? 'x^{2}' : `${c}x^{2}`;

      return expressionExercise({
        prompt: 'Simplify the difference quotient. Write your answer in terms of a and h.',
        latex: `f(x) = ${expr(quadratic, signedTerm(b, 'x'))}, \\qquad \\frac{f(a + h) - f(a)}{h}`,
        canonical: `${2 * c}*a + ${c}*h + (${b})`,
        summary: `Expanding the numerator gives ${2 * c}ah + ${c === 1 ? 'h²' : `${c}h²`} ${b < 0 ? '−' : '+'} ${Math.abs(b)}h. Every term has an h in it, so dividing by h leaves ${2 * c}a + ${c === 1 ? 'h' : `${c}h`} ${b < 0 ? '−' : '+'} ${Math.abs(b)}.`,
        hint: 'Expand the numerator first. Every term will carry a factor of h, which is what makes the division safe.',
        steps: [
          { label: 'Expand f(a + h).', latex: `${c === 1 ? '' : c}(a + h)^{2} ${b < 0 ? '-' : '+'} ${Math.abs(b)}(a + h)` },
          { label: 'Subtract f(a).', latex: `${2 * c}ah + ${c === 1 ? 'h^{2}' : `${c}h^{2}`} ${b < 0 ? '-' : '+'} ${Math.abs(b)}h` },
          { label: 'Divide by h — legal, because h is not zero.', latex: `${2 * c}a + ${c === 1 ? 'h' : `${c}h`} ${b < 0 ? '-' : '+'} ${Math.abs(b)}` },
        ],
        traps: [
          // The numerator handed in without the division.
          trap(`${2 * c}*a*h + ${c}*h*h + (${b})*h`, 'difference-quotient-not-divided'),
          // h dropped before the limit, which answers a different question.
          trap(`${2 * c}*a + (${b})`, 'average-rate-taken-as-instantaneous'),
        ],
      });
    },
  }),

  /* -------------------------------------------- tangent and the half angle */
  template({
    id: 'tangent-addition-value',
    conceptId: 'half-angle-formulas',
    description: 'Expand tan(A + B) from the two tangents.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const p = rng.int(1, 5);
      let q = rng.int(1, 5);
      // pq = 1 puts a zero in the denominator: A + B is 90° and the tangent is undefined.
      if (p * q === 1) q = q + 1;
      const answer = fraction(p + q, 1 - p * q);

      return fractionExercise({
        prompt: `tan A = ${p} and tan B = ${q}. Find tan(A + B).`,
        value: answer,
        requireSimplified: true,
        summary: `tan(A + B) = (tan A + tan B)/(1 − tan A tan B) = (${p} + ${q})/(1 − ${p * q}) = ${toText(answer)}.`,
        hint: 'The numerator adds the tangents. The denominator is 1 minus their product — and it is not optional.',
        steps: [
          { label: 'Write the formula.', latex: '\\tan(A + B) = \\frac{\\tan A + \\tan B}{1 - \\tan A\\tan B}' },
          { label: 'Substitute both tangents.', latex: `\\frac{${p} + ${q}}{1 - ${p}(${q})}` },
          { label: 'Simplify.', latex: `\\frac{${p + q}}{${1 - p * q}}` },
        ],
        // Distributing the tangent across the sum — the denominator dropped entirely.
        traps: [fractionTrap(fraction(p + q, 1), 'tangent-addition-distributed')],
      });
    },
  }),

  template({
    id: 'half-angle-cosine-sign',
    conceptId: 'half-angle-formulas',
    description: 'Find cos(A/2) where the quadrant decides the sign.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      /*
       * Built backwards from a triple so the root is rational. With
       * cos(A/2) = t, cos A = 2t² − 1, and A placed beyond 180° so that A/2
       * lands in the second quadrant and the cosine is negative — the one
       * arrangement where reading the quadrant of A instead of A/2 gives a
       * different answer rather than the same one.
       */
      const [leg, other, hyp] = rng.pick(TRIPLES);
      const t = rng.bool() ? fraction(leg, hyp) : fraction(other, hyp);
      const num = t.n;
      const den = t.d;
      const cosA = fraction(2 * num * num - den * den, den * den);
      const answer = fraction(-num, den);

      return fractionExercise({
        prompt: `A is an angle with 180° < A < 360° and cos A = ${toText(cosA)}. Find cos(A/2).`,
        value: answer,
        requireSimplified: true,
        summary: `cos²(A/2) = (1 + cos A)/2 = ${toText(fraction(num * num, den * den))}, so cos(A/2) = ±${toText(t)}. Halving the bounds gives 90° < A/2 < 180°, the second quadrant, where the cosine is negative — so the answer is ${toText(answer)}.`,
        hint: 'The formula gives the square. Halve the bounds on A to find which quadrant A/2 is in, and take the sign from there.',
        steps: [
          { label: 'Use the half-angle formula.', latex: `\\cos^{2}\\tfrac{A}{2} = \\frac{1 + ${toLatex(cosA)}}{2}` },
          { label: 'Simplify.', latex: `\\cos^{2}\\tfrac{A}{2} = ${toLatex(fraction(num * num, den * den))}` },
          { label: 'Take the root — sign not yet decided.', latex: `\\pm ${toLatex(t)}` },
          { label: 'Halve the bounds on A.', latex: '180^{\\circ} < A < 360^{\\circ} \\;\\Rightarrow\\; 90^{\\circ} < \\tfrac{A}{2} < 180^{\\circ}' },
          { label: 'Cosine is negative in the second quadrant.', latex: toLatex(answer) },
        ],
        // Reading the sign off A's quadrant rather than A/2's.
        traps: [fractionTrap(t, 'half-angle-sign-from-wrong-quadrant')],
      });
    },
  }),

  template({
    id: 'half-angle-formula-choice',
    conceptId: 'half-angle-formulas',
    description: 'Pick the identity that halves an angle.',
    difficulties: [2, 3],
    build: ({ rng }) =>
      choiceExercise({
        prompt: 'Which identity gives sin²(A/2)?',
        ...options(
          [
            { latex: '\\frac{1 - \\cos A}{2}', correct: true },
            { latex: '2\\sin A\\cos A', misconceptionId: 'half-angle-not-halved' },
            { latex: '\\frac{1 - \\cos A}{4}' },
            { latex: '\\frac{1 + \\cos A}{2}' },
          ],
          rng.shuffle,
        ),
        summary:
          'It is cos 2θ = 1 − 2sin²θ with θ = A/2, solved for the sine: sin²(A/2) = (1 − cos A)/2. The formula with the plus sign gives cos²(A/2), and 2 sin A cos A doubles an angle rather than halving one.',
        hint: 'Start from cos 2θ = 1 − 2sin²θ, put θ = A/2, and solve for the sine.',
      }),
  }),

  /* -------------------------------------------------------- the dot product */
  template({
    id: 'dot-product-value',
    conceptId: 'dot-product',
    description: 'Compute a dot product from components.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a1 = rng.int(-6, 8);
      const a2 = rng.int(-6, 8);
      const b1 = rng.int(-6, 8);
      const b2 = rng.int(-6, 8);
      const answer = a1 * b1 + a2 * b2;

      return choiceExercise({
        prompt: `Find a · b for a = (${minus(a1)}, ${minus(a2)}) and b = (${minus(b1)}, ${minus(b2)}).`,
        ...options(
          [
            { latex: `${answer}`, correct: true },
            { latex: `(${a1 * b1}, ${a2 * b2})`, misconceptionId: 'dot-product-left-as-vector' },
            { latex: `${a1 * b1}` },
            { latex: `${a1 * b2 + a2 * b1}` },
          ],
          rng.shuffle,
        ),
        summary: `Multiply matching components and add: ${a1}(${b1}) + ${a2}(${b2}) = ${a1 * b1} + ${a2 * b2} = ${answer}. The adding is what makes it a single number.`,
        hint: 'Multiply the matching components — and then add them.',
      });
    },
  }),

  template({
    id: 'perpendicular-missing-component',
    conceptId: 'dot-product',
    description: 'Find the component that makes two vectors perpendicular.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const p = rng.int(1, 6);
      const q = rng.int(1, 6);
      const r = p * rng.int(1, 4);
      const answer = -(q * r) / p;

      return numericExercise({
        prompt: `For what value of k are a = (${p}, ${q}) and b = (${minus(-r)}, k) perpendicular?`,
        value: answer,
        summary: `Perpendicular means the dot product is zero: ${p}(${minus(-r)}) + ${q}k = 0, so ${q}k = ${p * r} and k = ${minus(answer)}.`,
        hint: 'Two vectors are perpendicular exactly when their dot product is zero. Set it to zero and solve.',
        steps: [
          { label: 'Write the dot product.', latex: `${p}(${-r}) + ${q}k` },
          { label: 'Set it to zero.', latex: `${-p * r} + ${q}k = 0` },
          { label: 'Solve.', latex: `k = ${answer}` },
        ],
        // Matching components instead of testing the product.
        traps: [...(answer === -r ? [] : [trap(-r, 'perpendicular-tested-by-equality')])],
      });
    },
  }),

  template({
    id: 'dot-product-angle',
    conceptId: 'dot-product',
    description: 'Find the angle between two vectors.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const [a1, a2] = rng.pick(TRIPLES);
      const [b1, b2] = rng.pick(TRIPLES);
      const dot = a1 * b1 + a2 * b2;
      const magA = Math.sqrt(a1 * a1 + a2 * a2);
      const magB = Math.sqrt(b1 * b1 + b2 * b2);
      const answer = round1((Math.acos(dot / (magA * magB)) * 180) / Math.PI);
      const withoutMagnitudes = round1(dot);

      return numericExercise({
        prompt: `Find the angle between a = (${a1}, ${a2}) and b = (${b1}, ${b2}), in degrees to one decimal place.`,
        unit: '°',
        value: answer,
        tolerance: within(0.05, answer),
        summary: `a · b = ${dot}, |a| = ${magA}, |b| = ${magB}. So cos θ = ${dot}/${magA * magB}, and θ ≈ ${answer}°.`,
        hint: 'cos θ = (a · b) / (|a||b|). Both magnitudes go underneath.',
        steps: [
          { label: 'Dot the components.', latex: `${a1}(${b1}) + ${a2}(${b2}) = ${dot}` },
          { label: 'Both magnitudes.', latex: `|\\mathbf{a}| = ${magA}, \\quad |\\mathbf{b}| = ${magB}` },
          { label: 'Divide.', latex: `\\cos\\theta = \\frac{${dot}}{${magA * magB}}` },
          { label: 'Undo the cosine.', latex: `\\theta \\approx ${answer}^{\\circ}` },
        ],
        // The dot product reported as though it were the cosine, magnitudes forgotten.
        traps: [...(withoutMagnitudes === answer ? [] : [trap(withoutMagnitudes, 'dot-product-magnitudes-omitted')])],
      });
    },
  }),

  /* ------------------------------------------------------ the complex plane */
  template({
    id: 'complex-modulus',
    conceptId: 'complex-polar',
    description: 'Find the modulus of a complex number.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const [p, q, hyp] = rng.pick(TRIPLES);
      const real = rng.bool() ? p : -p;
      const imaginary = rng.bool() ? q : -q;

      return numericExercise({
        prompt: `Find the modulus of z = ${minus(real)} ${imaginary < 0 ? '−' : '+'} ${Math.abs(imaginary)}i.`,
        value: hyp,
        summary: `|z| = √(${real}² + ${imaginary}²) = √${p * p + q * q} = ${hyp} — the distance from the origin to the point (${minus(real)}, ${minus(imaginary)}).`,
        hint: 'The modulus is the distance from the origin: √(a² + b²).',
        // Adding the parts instead of using Pythagoras — the same error as vector magnitude.
        traps: [
          ...(Math.abs(real) + Math.abs(imaginary) === hyp
            ? []
            : [trap(Math.abs(real) + Math.abs(imaginary), 'vector-magnitude-as-sum')]),
        ],
      });
    },
  }),

  template({
    id: 'complex-divide-conjugate',
    conceptId: 'complex-polar',
    description: 'Divide one complex number by another.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      // Built from the answer outwards, so the division comes out whole.
      const c = rng.int(1, 4);
      const d = rng.int(-4, 4) || 1;
      const p = rng.int(1, 5);
      const q = rng.int(-5, 5) || 2;
      const a = p * c - q * d;
      const b = p * d + q * c;
      const componentwise = `\\frac{${a}}{${c}} ${b * d < 0 ? '-' : '+'} \\frac{${Math.abs(b)}}{${Math.abs(d)}}i`;

      return choiceExercise({
        prompt: 'Evaluate the quotient.',
        latex: `\\frac{${expr(`${a}`, signedTerm(b, 'i')).replace(/^\+\s*/, '')}}{${expr(`${c}`, signedTerm(d, 'i')).replace(/^\+\s*/, '')}}`,
        ...options(
          [
            { latex: `${expr(`${p}`, signedTerm(q, 'i')).replace(/^\+\s*/, '')}`, correct: true },
            { latex: componentwise, misconceptionId: 'complex-division-componentwise' },
            { latex: `${expr(`${p}`, signedTerm(-q, 'i')).replace(/^\+\s*/, '')}` },
            { latex: `${expr(`${q}`, signedTerm(p, 'i')).replace(/^\+\s*/, '')}` },
          ],
          rng.shuffle,
        ),
        summary: `Multiply top and bottom by the conjugate of the denominator, ${minus(c)} ${-d < 0 ? '−' : '+'} ${Math.abs(d)}i. The bottom becomes ${c * c + d * d}, a real number, and the top works out to ${minus(`${p * (c * c + d * d)}`)} + ${q * (c * c + d * d)}i — giving ${minus(`${p}`)} ${q < 0 ? '−' : '+'} ${Math.abs(q)}i.`,
        hint: 'Multiply top and bottom by the conjugate of the denominator, which makes the bottom real.',
      });
    },
  }),

  template({
    id: 'de-moivre-modulus',
    conceptId: 'complex-polar',
    description: 'Find the modulus of a power of a complex number.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const r = rng.int(2, 4);
      const n = rng.int(2, 4);
      const angle = rng.int(1, 8) * 10;
      const answer = r ** n;

      return numericExercise({
        prompt: `z has modulus ${r} and argument ${angle}°. What is the modulus of z^${n}?`,
        value: answer,
        summary: `De Moivre: z^${n} has modulus ${r}^${n} = ${answer} and argument ${n} × ${angle}° = ${n * angle}°. The modulus is raised to the power; only the argument is multiplied by it.`,
        hint: 'One of the two is raised to the power and the other is multiplied by it. Which is which?',
        steps: [
          { label: 'De Moivre’s theorem.', latex: `z^{${n}} = r^{${n}}\\big(\\cos ${n}\\theta + i\\sin ${n}\\theta\\big)` },
          { label: 'Raise the modulus.', latex: `${r}^{${n}} = ${answer}` },
        ],
        traps: [
          // The modulus left unraised — De Moivre applied to the angle alone.
          ...(r === answer ? [] : [trap(r, 'de-moivre-modulus-not-raised')]),
          // The modulus multiplied by n instead of raised to it.
          ...(r * n === answer ? [] : [trap(r * n, 'de-moivre-modulus-not-raised')]),
        ],
      });
    },
  }),
];
